import React, { useState, useEffect } from "react";
import { Modal, Space, Button, Typography, Select, Spin } from "antd";
import { TestSetService } from "../../../services/TestSetService";
import { TestSet } from "../../../model_gen/evaluation";
import { DatasetService } from "../../../services/DatasetService";
import { FinetuneDataset } from "../../../model_gen/finetuning";

const { Text } = Typography;

interface AddSemanticLayerModalProps {
  isVisible: boolean;
  onClose: () => void;
  onAddLayer: (layerData: {
    entityType: string;
    entityTypeId: number;
  }) => void;
  datasetId?: number;
  testsetId?: number;
  apiKey: string;
}

const AddSemanticLayerModal: React.FC<AddSemanticLayerModalProps> = ({
  isVisible,
  onClose,
  onAddLayer,
  datasetId,
  testsetId,
  apiKey
}) => {
  const [layerType, setLayerType] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [layerOptions, setLayerOptions] = useState<{ value: string; label: string }[]>([]);
  const [entityOptions, setEntityOptions] = useState<TestSet[] | FinetuneDataset[]>([]);
  const [selectedEntityId, setSelectedEntityId] = useState<number | undefined>(undefined);
  const [loadingEntity, setLoadingEntity] = useState<boolean>(false);

  // Set layer options based on datasetId or testsetId
  useEffect(() => {
    if (datasetId) {
      setLayerOptions([{ value: "test_set", label: "Test Set" }]);
    } else if (testsetId) {
      setLayerOptions([{ value: "dataset", label: "Dataset" }]);
    } else {
      setLayerOptions([
        { value: "test_set", label: "Test Set" },
        { value: "dataset", label: "Dataset" }
      ]);
    }
  }, [datasetId, testsetId]);

  // Fetch test sets when layer type is "test_set" and datasetId is available
  useEffect(() => {
    if (layerType === "test_set" && datasetId) {
      setLoadingEntity(true);
      setSelectedEntityId(undefined);
      
      const subscription = TestSetService.getTestSetsByDatasetId(datasetId, setLoadingEntity, apiKey)
        .subscribe({
          next: (response: any) => {
            if (response) {
              setEntityOptions(response);
            } else {
              setEntityOptions([]);
            }
            setLoadingEntity(false);
          },
          error: (error) => {
            console.error("Error fetching test sets:", error);
            setEntityOptions([]);
            setLoadingEntity(false);
          }
        });
      
      return () => subscription.unsubscribe();
    }
  }, [layerType, datasetId, apiKey]);

  useEffect(() => {
    if (layerType === "dataset" && testsetId) {
      setLoadingEntity(true);
      setSelectedEntityId(undefined);
      
      const subscription = DatasetService.getDatasetsByTestSet(testsetId, setLoadingEntity, apiKey)
        .subscribe({
          next: (response: any) => {
            if (response) {
              setEntityOptions(response);
            } else {
              setEntityOptions([]);
            }
            setLoadingEntity(false);
          },
          error: (error) => {
            console.error("Error fetching datasets:", error);
            setEntityOptions([]);
            setLoadingEntity(false);
          }
        });
      
      return () => subscription.unsubscribe();
    }
  }, [layerType, testsetId, apiKey]);

  const getEntityDisplayName = (entity: TestSet | FinetuneDataset) => {
    if (layerType === "test_set") {
      return entity.name;
    } else if (layerType === "dataset") {
      return entity.name;
    }
  };

  const handleSubmit = () => {
    if (!layerType) {
      return;
    }

    if (!selectedEntityId) {
      return;
    }

    if (isSubmitting) {
      return; // Prevent multiple submissions
    }

    setIsSubmitting(true);
    
    // Call the onAddLayer callback with the selected entity type and ID
    onAddLayer({
      entityType: layerType,
      entityTypeId: selectedEntityId
    });
    
    // Reset form and close modal
    resetForm();
    onClose();
    setIsSubmitting(false);
  };

  const resetForm = () => {
    setLayerType("");
    setSelectedEntityId(undefined);
    setIsSubmitting(false);
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  return (
    <Modal
      title="Add Semantic Layer"
      open={isVisible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel} disabled={isSubmitting}>
          Cancel
        </Button>,
        <Button 
          key="submit" 
          type="primary" 
          onClick={handleSubmit}
          disabled={!layerType || !selectedEntityId || isSubmitting || loadingEntity}
          loading={isSubmitting}
        >
          Add Layer
        </Button>,
      ]}
    >
      <Space direction="vertical" style={{ width: "100%" }}>
        <div style={{ marginTop: "16px" }}>
          <Text strong>Layer Type</Text>
          <Select
            style={{ width: "100%" }}
            placeholder="Select layer type"
            value={layerType || undefined}
            onChange={(value) => setLayerType(value)}
            options={layerOptions}
          />
        </div>

        {layerType === "test_set" && (
          <div style={{ marginTop: "16px" }}>
            <Text strong>Test Set</Text>
            <Select
              style={{ width: "100%" }}
              placeholder="Select test set"
              value={selectedEntityId}
              onChange={(value) => setSelectedEntityId(value)}
              loading={loadingEntity}
              disabled={loadingEntity}
              options={entityOptions.map(entity => ({
                value: entity.id,
                label: getEntityDisplayName(entity)
              }))}
              notFoundContent={loadingEntity ? <Spin size="small" /> : "No entities found"}
            />
          </div>
        )}

        {layerType === "dataset" && (
          <div style={{ marginTop: "16px" }}>
            <Text strong>Dataset</Text>
            <Select
              style={{ width: "100%" }}
              placeholder="Select dataset"
              value={selectedEntityId}
              onChange={(value) => setSelectedEntityId(value)}
              loading={loadingEntity}
              disabled={loadingEntity}
              options={entityOptions.map(entity => ({
                value: entity.id,
                label: getEntityDisplayName(entity)
              }))}
              notFoundContent={loadingEntity ? <Spin size="small" /> : "No entities found"}
            />
          </div>
        )}
      </Space>
    </Modal>
  );
};

export default AddSemanticLayerModal; 